<template>
  <section class="map-section">
    <v-map :zoom="zoom" :center="center" :options="options" ref="map" class="map-section__leaflet" v-once>
      <v-geojson :geojson="geojson.content" :options="geojson.options" ref="geojson"></v-geojson>
      <v-marker :lat-lng="marker.latlng" :icon="marker.icon">
        <v-popup :content="marker.popup"></v-popup>
      </v-marker>
      <span class="map-section__leaflet__source">
        Source:&nbsp;Mauritius Revenue Authority, PwC, PKF International |
        Interactive published: November 7, 2017 |
        Map:&nbsp;<a href="http://leafletjs.com" title="A JS library for interactive maps">Leaflet</a>
      </span>
    </v-map>
    <form class="map-section__panel">
      <h6 class="map-section__panel__heading">
        Tax treaty status
      </h6>
      <p class="p-3 bg-light m-0">
        <strong>Welcome to Mauritius: Africa’s offshore tax hub.</strong>
        Where one country has a tax treaty with Mauritius that includes lower
        tax rates for Mauritian companies, that treaty can help companies save
        millions in tax and move money out of Africa.
      </p>
      <ul class="list-unstyled map-section__panel__pills row">
        <li v-for="name in statuses" class="list-unstyled-item map-section__panel__pills__item col-sm-6 col-md-12">
          <i class="fa fa-circle mr-2" :style="{ color: color(name) }"></i> {{ name }}
        </li>
      </ul>
    </form>
  </section>
</template>

<script>
import SETTINGS from '@/utils/settings.js'
import taxTreaties from '@/utils/tax-treaties.js'
import CountryPopup from '@/components/CountryPopup.vue'
import SmoothScroll from 'smooth-scroll'
import L from 'leaflet'
import Vue from 'vue'
import _filter from 'lodash/filter'

export default {
  name: 'MapSection',
  methods: {
    countFor (status) {
      return _filter(taxTreaties.raw, { 'tax-treaty-status': status }).length
    },
    resetGeojsonStyle () {
      const geojsonLayer = this.$refs.geojson
      return geojsonLayer.mapObject.eachLayer(layer => {
        return geojsonLayer.mapObject.resetStyle(layer)
      })
    },
    zoomToGeojson () {
      const map = this.$refs.map
      const geojsonLayer = this.$refs.geojson
      if (map && geojsonLayer) {
        const bounds = geojsonLayer.getBounds()
        map.fitBounds(bounds)
      }
    },
    color (name) {
      return taxTreaties.SCHEME[name]
    },
    openCountryPopup (iso3 = null, scroll = true) {
      this.$refs.geojson.mapObject.eachLayer(layer => {
        if (layer.feature.properties.adm0_a3 === iso3) {
          layer.fire('click')
        }
      })
      if (scroll) {
        this.animateScroll(document.getElementsByClassName('map-section')[0])
      }
    }
  },
  mounted () {
    this.zoomToGeojson()
    // Open the first pop
    // this.openCountryPopup(taxTreaties.first.iso3, false)
    // Watch for event to open a country popup
    taxTreaties.bus.$on('open-country-popup', this.openCountryPopup)
  },
  computed: {
    indicator: {
      cache: false,
      get () {
        return taxTreaties.indicator
      },
      set (indicator) {
        taxTreaties.indicator = indicator
        // Reset geojson style
        this.resetGeojsonStyle()
        // Center the map again
        this.zoomToGeojson()
        // Force data update
        this.$forceUpdate()
        // Return the indicator
        return indicator
      }
    },
    animateScroll () {
      // Create a scrolling context
      const scroll = new SmoothScroll(null, { offset: 0 })
      // Return the animateScroll function
      return scroll.animateScroll
    }
  },
  data () {
    return {
      statuses: SETTINGS.indicators['tax-treaty-status'],
      zoom: 3,
      center: [-9.6, 37],
      options: {
        scrollWheelZoom: false,
        attributionControl: false
      },
      tilelayer: {
        url: '//{s}.tile.osm.org/{z}/{x}/{y}.png'
      },
      marker: {
        latlng: [-20.348404, 57.552152],
        popup: '<div class="p-2 text-center h5 m-0 text-uppercase bg-dark with-tip">Mauritius</div>',
        icon: L.icon({
          iconUrl: require('@/assets/images/mauritius.svg'),
          iconSize: [31, 45],
          popupAnchor: [0, -45 / 2]
        })
      },
      geojson: {
        content: require('@/assets/africa.geo.json'),
        options: {
          style: (feature, i) => {
            return {
              weight: 1,
              fillOpacity: 1,
              color: taxTreaties.color(feature.properties.adm0_a3)
            }
          },
          onEachFeature (feature, layer) {
            const iso3 = feature.properties.adm0_a3
            const name = feature.properties.name
            // Create a component
            const vm = new Vue(Object.assign({ }, CountryPopup, { propsData: { iso3, name } }))
            // Mount the vue
            vm.$mount()
            // Bind the component to the layer
            layer.bindPopup(vm.$el)
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
  .map-section {
    position: relative;
    background: white;
    min-height: 90vh;

    &__panel {
      z-index: 100;
      position: absolute;
      right: 10vw;
      top: 10vh;
      width: 330px;
      box-shadow: 0 0 5px 0 rgba(black, 0.3);
      padding:0;

      @include media-breakpoint-down(sm) {
        position: static;
      }

      @include media-breakpoint-down(sm) {
        top:auto;
        bottom:0;
        right:0;
        left:0;
        width: 100%;
        padding: $spacer * 0.5;
        background: theme-color('cuttysark');
      }

      @include media-breakpoint-only(md) {
        top:2vw;
        right:2vw;
      }

      &__heading {
        background: theme-color('cuttysark');
        padding: $spacer;
        margin:0;
        color: white;
      }

      &__pills {
        margin:0;
        background: theme-color('secondary');

        @include media-breakpoint-down(sm) {
          font-size: 0.9rem;
        }

        &__item {
          padding-bottom: $spacer * 0.5;
          border-bottom: 1px solid darken(theme-color('secondary'), 10%);
          padding:$spacer;

          @include media-breakpoint-down(sm) {
            padding: $spacer * 0.25 $spacer;
          }

          &.active > button {
            color: white;
            background: theme-color('primary');
          }
        }
      }
    }

    &__leaflet {
      &__source {
        position:  absolute;
        bottom:0;
        left:0;
        font-size: 11px;
        padding: 0 5px;
        max-width: calc(100% - 60px);
        a {
          color: $link-color;
        }
      }
    }


    & &__leaflet.vue2leaflet-map.leaflet-container {
      z-index: 50;
      background: inherit;
      padding:0;
      position: absolute;
      top:0;
      bottom:0;
      left:0;
      right: calc(10vw + 330px + 10px);
      width: auto;

      @include media-breakpoint-down(sm) {
        position: relative;
        height:90vh;
      }

      @include media-breakpoint-down(sm) {
        right: 0;
      }

      @include media-breakpoint-only(md) {
        right: calc(2vw + 330px + 10px);
      }

      .leaflet-pane.leaflet-overlay-pane > svg > g > path.leaflet-interactive {
        transition: stroke 2s, fill 2s;
      }
    }

  }
</style>



// WEBPACK FOOTER //
// MapSection.vue?44438a43